import { PushPlatformHint, type PushSubscription } from '@gb-ticket/shared-types';
import { Loader2Icon, Trash2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiRequestError } from '@/lib/api';
import { useVoice, useVoiceCopy } from '@/platform/voice/use-voice';
import { formatDateTime } from '@/lib/format';

import { postPushTest } from './push-api';
import { PushIosInstallHint, PUSH_ASK_PERMISSION_BUTTON_LABEL } from './push-ios-hint';
import { PushSettingsGuidePanel } from './push-settings-guide';
import {
  PUSH_PLATFORM_HINT_LABELS,
  reportPushActivationError,
  type PushPermissionState,
  type PushSettingsGuide,
} from './push-support';
import { usePushSubscription } from './use-push-subscription';

export { PUSH_ASK_PERMISSION_BUTTON_LABEL };

function isVapidMismatchError(lastErrorCode: string | null | undefined): boolean {
  return lastErrorCode === '401' || lastErrorCode === '403';
}

function deliveryIssueHint(lastErrorCode: string | null | undefined): string | null {
  if (isVapidMismatchError(lastErrorCode)) {
    return 'Der Server-Schlüssel passt nicht mehr zu diesem Gerät. Bitte Push erneut aktivieren.';
  }
  if (lastErrorCode === '410' || lastErrorCode === '404') {
    return 'Der Push-Dienst kennt dieses Gerät nicht mehr. Bitte erneut aktivieren.';
  }
  if (lastErrorCode != null && lastErrorCode !== '') {
    return 'Der letzte Versuch ist fehlgeschlagen. Bitte erneut aktivieren oder das Gerät entfernen.';
  }
  return null;
}

function permissionStatusLabel(
  supported: boolean,
  serverEnabled: boolean,
  permission: PushPermissionState,
  currentDevice: PushSubscription | undefined,
  isRegisteredOnThisDevice: boolean,
): string {
  if (!supported) {
    return 'Nicht unterstützt';
  }
  if (!serverEnabled) {
    return 'Derzeit nicht verfügbar';
  }
  if (permission === 'denied') {
    return 'Blockiert';
  }
  if (isRegisteredOnThisDevice && isVapidMismatchError(currentDevice?.lastErrorCode)) {
    return 'Registriert, Zustellung fehlgeschlagen';
  }
  if (isRegisteredOnThisDevice && currentDevice?.lastErrorCode) {
    return 'Registriert, letzter Versand fehlgeschlagen';
  }
  if (isRegisteredOnThisDevice) {
    return 'Aktiv auf diesem Gerät';
  }
  if (permission === 'granted') {
    return 'Berechtigung erteilt, noch nicht registriert';
  }
  return 'Nicht aktiv';
}

export function PushDevicesCard() {
  const copy = useVoiceCopy();
  const voice = useVoice();
  const {
    supported,
    serverEnabled,
    permission,
    isActiveOnThisDevice,
    currentEndpoint,
    devices,
    isLoading,
    isEnabling,
    isRemoving,
    enablePush,
    removeDevice,
    refreshCurrentEndpoint,
  } = usePushSubscription(true);
  const [isTesting, setIsTesting] = useState(false);
  const [serviceGuide, setServiceGuide] = useState<PushSettingsGuide | null>(null);
  const currentDevice = devices.find((device) => device.endpoint === currentEndpoint);
  const deliveryBlocked = isVapidMismatchError(currentDevice?.lastErrorCode);
  const isHealthyActive = isActiveOnThisDevice && !deliveryBlocked;
  const isRegisteredOnThisDevice = isActiveOnThisDevice;
  const deliveryHint = isRegisteredOnThisDevice
    ? deliveryIssueHint(currentDevice?.lastErrorCode)
    : null;

  useEffect(() => {
    void refreshCurrentEndpoint();
  }, [refreshCurrentEndpoint]);

  async function onEnablePush(permissionPromise: Promise<NotificationPermission>) {
    try {
      await enablePush(permissionPromise);
      setServiceGuide(null);
      toast.success('Push-Benachrichtigungen sind auf diesem Gerät aktiv.');
    } catch (error) {
      if (error instanceof ApiRequestError && error.error.code === 'PUSH_PERMISSION_DENIED') {
        if (Notification.permission === 'denied') {
          toast.error(copy('platform.push.blocked'));
        } else {
          toast.error('Ohne Erlaubnis kann diese App keine Mitteilungen senden.');
        }
        return;
      }
      setServiceGuide(reportPushActivationError(error, voice));
    }
  }

  async function onRemoveDevice(deviceId: string) {
    try {
      await removeDevice(deviceId);
      toast.success('Gerät entfernt.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Gerät konnte nicht entfernt werden.',
      );
    }
  }

  async function onTestPush() {
    setIsTesting(true);
    try {
      await postPushTest();
      toast.success('Test-Mitteilung wurde gesendet.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Test-Mitteilung konnte nicht gesendet werden.',
      );
    } finally {
      setIsTesting(false);
    }
  }

  const canEnable =
    supported &&
    serverEnabled &&
    permission !== 'denied' &&
    (!isRegisteredOnThisDevice || deliveryBlocked || Boolean(currentDevice?.lastErrorCode));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Push auf diesem Gerät</CardTitle>
        <CardDescription>
          System-Mitteilungen erscheinen auch, wenn der Browser im Hintergrund ist oder geschlossen
          wurde.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Push-Status wird geladen …
          </p>
        ) : (
          <>
            <p className="text-sm">
              Status:{' '}
              <span className="font-medium">
                {permissionStatusLabel(
                  supported,
                  serverEnabled,
                  permission,
                  currentDevice,
                  isRegisteredOnThisDevice,
                )}
              </span>
            </p>

            {deliveryHint ? <p className="text-sm text-muted-foreground">{deliveryHint}</p> : null}

            <PushIosInstallHint />
            {serviceGuide ? <PushSettingsGuidePanel guide={serviceGuide} /> : null}

            {supported && !serverEnabled ? (
              <p className="text-sm text-muted-foreground">
                Push ist auf dem Server ausgeschaltet. Die Browser-Frage, ob diese App Mitteilungen
                senden darf, erscheint erst, wenn Push verfügbar ist.
              </p>
            ) : null}

            {permission === 'denied' ? (
              <p className="text-sm text-muted-foreground">
                {copy('platform.push.blocked.browser')}
              </p>
            ) : null}

            {canEnable ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Ein Klick öffnet die Browser-Frage, ob diese App Mitteilungen senden darf.
                </p>
                <Button
                  type="button"
                  disabled={isEnabling}
                  onClick={() => {
                    const permissionPromise = Notification.requestPermission();
                    void onEnablePush(permissionPromise);
                  }}
                >
                  {isEnabling ? <Loader2Icon className="size-4 animate-spin" aria-hidden /> : null}
                  {PUSH_ASK_PERMISSION_BUTTON_LABEL}
                </Button>
              </div>
            ) : null}

            {isHealthyActive ? (
              <Button
                type="button"
                variant="outline"
                disabled={isTesting}
                onClick={() => void onTestPush()}
              >
                {isTesting ? <Loader2Icon className="size-4 animate-spin" aria-hidden /> : null}
                Test an dieses Gerät
              </Button>
            ) : null}

            {devices.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">Registrierte Geräte</p>
                <ul className="divide-y rounded-md border">
                  {devices.map((device) => {
                    const isCurrent = device.endpoint === currentEndpoint;
                    const label = device.platformHint
                      ? PUSH_PLATFORM_HINT_LABELS[device.platformHint]
                      : PUSH_PLATFORM_HINT_LABELS[PushPlatformHint.OTHER];
                    const deviceHint = deliveryIssueHint(device.lastErrorCode);
                    return (
                      <li
                        key={device.id}
                        className="flex items-start justify-between gap-3 px-3 py-3 text-sm"
                      >
                        <div className="min-w-0">
                          <p className="font-medium">
                            {label}
                            {isCurrent ? ' (dieses Gerät)' : ''}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {device.userAgent ?? 'Unbekannter Browser'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Registriert: {formatDateTime(device.createdAt)}
                          </p>
                          {device.lastErrorCode ? (
                            <p className="text-xs text-muted-foreground">
                              Letzter Fehler: {device.lastErrorCode}
                              {deviceHint ? ` — ${deviceHint}` : ''}
                            </p>
                          ) : null}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="shrink-0 text-destructive hover:text-destructive"
                          disabled={isRemoving}
                          aria-label={`Gerät ${label} entfernen`}
                          onClick={() => void onRemoveDevice(device.id)}
                        >
                          <Trash2Icon className="size-4" aria-hidden />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : serverEnabled && supported ? (
              <p className="text-sm text-muted-foreground">Noch kein Gerät für Push registriert.</p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
